import React from "react";
import { motion } from "motion/react";
import { CheckCircle2, ChevronLeft, ChevronRight, LayoutGrid, AlertTriangle } from "lucide-react";
import { SendIcon } from "@/components/icons/actions";
import { ChartBarIcon, SuccessIcon, DraftIcon } from "@/components/icons/status";
import { demoColumnLabel, demoWeek } from "../demoCalendar";

interface Props {
  reducedMotion?: boolean;
}

// La scène prépare la semaine suivante (S+1), du lundi au samedi.
const week = demoWeek(1, new Date(), 5);
const days = week.days.slice(0, 6).map(demoColumnLabel);

const employees = [
  {
    name: "Marie D.",
    role: "Hôtesse de caisse",
    initials: "MD",
    shifts: ["09:00–17:00", "TYPING", "09:00–17:00", "Repos", "10:00–18:00", "—"],
  },
  {
    name: "Lucas B.",
    role: "Employé polyvalent",
    initials: "LB",
    shifts: ["07:00–14:00", "07:00–14:00", "07:00–14:00", "Repos", "07:00–14:00", "07:00–14:00"],
  },
  {
    name: "Sarah M.",
    role: "Adjointe",
    initials: "SM",
    shifts: ["14:00–21:00", "Congé", "Congé", "14:00–21:00", "14:00–21:00", "Repos"],
  },
  {
    name: "Tom P.",
    role: "Hôte de caisse",
    initials: "TP",
    shifts: ["09:00–17:00", "09:00–17:00", "Repos", "09:00–17:00", "09:00–17:00", "09:00–15:00"],
  },
];

const TypingCell: React.FC<{ instant?: boolean }> = ({ instant }) => {
  const target = "09:00–13:00";
  const target2 = "14:00–17:00";
  const [text1, setText1] = React.useState(instant ? target : "");
  const [text2, setText2] = React.useState(instant ? target2 : "");
  const [showTotal, setShowTotal] = React.useState(instant);

  React.useEffect(() => {
    if (instant) return;
    // Tous les minuteurs sont suivis : si la scène change en pleine saisie, rien ne continue en arrière-plan.
    const intervals: ReturnType<typeof setInterval>[] = [];
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    let i = 0;
    const t1 = setInterval(() => {
      i++;
      setText1(target.slice(0, i));
      if (i >= target.length) {
        clearInterval(t1);
        let j = 0;
        timeouts.push(
          setTimeout(() => {
            const t2 = setInterval(() => {
              j++;
              setText2(target2.slice(0, j));
              if (j >= target2.length) {
                clearInterval(t2);
                timeouts.push(setTimeout(() => setShowTotal(true), 200));
              }
            }, 70);
            intervals.push(t2);
          }, 400),
        );
      }
    }, 70);
    intervals.push(t1);
    return () => {
      intervals.forEach(clearInterval);
      timeouts.forEach(clearTimeout);
    };
  }, [instant]);

  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="text-[10px] font-semibold text-foreground bg-primary/10 border border-primary/30 rounded px-1.5 py-0.5 text-center">
        {text1 || <span className="opacity-30">––:–– ––:––</span>}
      </div>
      <div className="text-[10px] font-semibold text-foreground bg-primary/10 border border-primary/30 rounded px-1.5 py-0.5 text-center relative">
        {text2 || <span className="opacity-30">––:–– ––:––</span>}
        {!instant && text2.length < target2.length && (
          <motion.span
            animate={{ opacity: [1, 0] }}
            transition={{ duration: 0.6, repeat: Infinity }}
            className="absolute top-1 right-1.5 w-[1px] h-2.5 bg-primary"
          />
        )}
      </div>
      {showTotal && (
        <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} className="text-[10px] font-bold text-success text-center">
          7h00 ✓
        </motion.div>
      )}
    </div>
  );
};

export const PlanningScene: React.FC<Props> = ({ reducedMotion }) => {
  const [published, setPublished] = React.useState(false);
  const [pulse, setPulse] = React.useState(false);

  React.useEffect(() => {
    const t1 = setTimeout(() => setPulse(true), reducedMotion ? 0 : 5000);
    const t2 = setTimeout(() => setPublished(true), reducedMotion ? 200 : 6500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [reducedMotion]);

  return (
    <div className="px-3 md:px-6 pb-6 pt-3">
      {/* Sticky glass header bar — mirrors src/pages/Planning.tsx */}
      <div className="glass-subtle shadow-glass rounded-xl border border-border/50 px-3 py-2 mb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-[15px] font-bold text-foreground tracking-tight">Planning</h1>

            {/* Segmented Planning / Couverture */}
            <div className="hidden sm:flex bg-muted/60 p-1 rounded-lg ring-1 ring-border/30 gap-1">
              <span className="h-6 px-2.5 inline-flex items-center gap-1 text-[11px] font-medium bg-background shadow-sm ring-1 ring-border/40 text-foreground rounded-md">
                <LayoutGrid className="h-3 w-3" /> Planning
              </span>
              <span className="h-6 px-2.5 inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground rounded-md">
                <ChartBarIcon className="h-3 w-3" /> Couverture
              </span>
            </div>

            <div className="hidden sm:block h-4 w-px bg-border/40" />

            {/* Week navigation */}
            <div className="flex items-center gap-1">
              <button className="h-6 w-6 rounded-md hover:bg-muted/60 flex items-center justify-center text-muted-foreground">
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <span className="text-[12px] font-semibold text-foreground px-1">Sem. {week.number} · {week.label}</span>
              <button className="h-6 w-6 rounded-md hover:bg-muted/60 flex items-center justify-center text-muted-foreground">
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Publication badge */}
            <motion.span
              key={published ? "pub" : "draft"}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`h-5 px-1.5 inline-flex items-center gap-1 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${
                published
                  ? "bg-success/10 text-success border-success/25"
                  : "bg-warning/10 text-warning border-warning/25"
              }`}
            >
              {published ? <SuccessIcon className="h-2.5 w-2.5" /> : <DraftIcon className="h-2.5 w-2.5" />}
              {published ? "Publié" : "Brouillon"}
            </motion.span>
          </div>

          {/* Right actions — mini PlanningActions */}
          <div className="flex items-center gap-1.5">
            <button className="hidden sm:inline-flex h-7 px-2.5 text-[11px] font-medium rounded-md text-muted-foreground hover:bg-muted/60 items-center">
              Modèle
            </button>
            <button className="hidden md:inline-flex h-7 px-2.5 text-[11px] font-medium rounded-md text-muted-foreground hover:bg-muted/60 items-center">
              Imprimer
            </button>
            <motion.button
              animate={pulse && !published ? { scale: [1, 1.05, 1], boxShadow: ["0 0 0 0 hsl(var(--primary)/0.5)", "0 0 0 8px hsl(var(--primary)/0)", "0 0 0 0 hsl(var(--primary)/0)"] } : {}}
              transition={{ duration: 1.2, repeat: published ? 0 : Infinity }}
              className={`h-7 px-3 rounded-md text-[11px] font-semibold flex items-center gap-1.5 shadow-sm transition-colors ${
                published ? "bg-success text-white" : "bg-primary text-primary-foreground"
              }`}
            >
              {published ? <CheckCircle2 className="w-3.5 h-3.5" /> : <SendIcon className="w-3 h-3" />}
              {published ? "Publié" : "Publier"}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Compliance hint */}
      <motion.div
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: reducedMotion ? 0 : 1.5 }}
        className="mb-3 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-warning/8 border border-warning/25 text-[11px] text-foreground"
      >
        <AlertTriangle className="w-3 h-3 text-warning shrink-0" />
        <span><span className="font-semibold">Conformité IDCC 1505</span> — amplitudes, repos quotidien et heures sup vérifiés en temps réel</span>
      </motion.div>

      {/* Planning grid */}
      <div className="bg-card border rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <div className="min-w-[640px]">
            <div className="grid grid-cols-[180px_repeat(6,1fr)] bg-muted/20 border-b text-[11px] font-medium">
              <div className="p-2.5 text-muted-foreground">Équipe</div>
              {days.map((d) => (
                <div key={d} className="p-2.5 text-center text-foreground border-l border-border/50">
                  {d}
                </div>
              ))}
            </div>
            <div className="divide-y">
              {employees.map((row) => (
                <div key={row.name} className="grid grid-cols-[180px_repeat(6,1fr)]">
                  <div className="p-2 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold shrink-0">{row.initials}</div>
                    <div className="min-w-0">
                      <div className="text-[12px] font-semibold text-foreground leading-tight truncate">{row.name}</div>
                      <div className="text-[10px] text-muted-foreground truncate">{row.role}</div>
                    </div>
                  </div>
                  {row.shifts.map((s, i) => (
                    <div key={i} className="p-1.5 border-l border-border/50 flex items-center justify-center min-h-[52px]">
                      {s === "TYPING" ? (
                        <TypingCell instant={reducedMotion} />
                      ) : s === "Repos" ? (
                        <div className="w-full h-full flex items-center justify-center text-[10px] font-bold rounded bg-muted/40 text-muted-foreground">Repos</div>
                      ) : s === "Congé" ? (
                        <div className="w-full h-full flex items-center justify-center text-[10px] font-bold rounded bg-warning/10 text-warning border border-warning/30">Congé</div>
                      ) : s === "—" ? (
                        <div className="text-[10px] text-muted-foreground">—</div>
                      ) : (
                        <div className="text-[10px] font-semibold text-foreground bg-primary/5 border border-primary/20 rounded px-1.5 py-1 w-full text-center">
                          {s}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Toast post-publication */}
      {published && (
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          className="mt-4 flex items-center gap-3 px-4 py-3 rounded-xl bg-success/10 border border-success/30 shadow-sm"
        >
          <div className="w-8 h-8 rounded-full bg-success/20 flex items-center justify-center">
            <CheckCircle2 className="w-4 h-4 text-success" />
          </div>
          <div>
            <div className="text-xs font-semibold text-foreground">Planning publié</div>
            <div className="text-[11px] text-muted-foreground">4 salariés notifiés par email · snapshot légal IDCC 1505 conservé</div>
          </div>
        </motion.div>
      )}
    </div>
  );
};
